import React, { useState, useContext } from 'react';
import api from '../../services/api';
import { AuthContext } from '../../contexts/AuthContext';
import { shortDate } from './constants';

// Connecting your own mailbox.
//
// A rep supplies an address and a password and nothing else -- the server works out the
// hostname, ports, and TLS from the domain. Asking a coach-facing salesperson for an
// IMAP port is how a feature like this ends up unused.
//
// The password goes straight to the server, is tested against the real mail host before
// anything is written, and is encrypted at rest. It is never held in component state
// after the request returns.

const StatusPill = ({ status }) => {
  const tone = status === 'connected' ? 'ok' : status === 'error' ? 'bad' : 'idle';
  const label = status === 'connected' ? 'Connected'
    : status === 'error' ? 'Needs attention'
      : 'Not connected';
  return <span className={`mbx-pill ${tone}`}>{label}</span>;
};

const ConnectForm = ({ initialEmail, existing, onDone, onCancel }) => {
  const [email, setEmail] = useState(existing?.email_address || initialEmail || '');
  const [password, setPassword] = useState('');
  const [scope, setScope] = useState(existing?.sync_scope || 'all');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const r = await api.post('/crm/mailboxes', {
        email_address: email.trim(),
        password,
        sync_scope: scope,
      });
      setPassword('');
      onDone(r.data);
    } catch (err) {
      const data = err.response?.data || {};
      setError({
        message: data.message || 'Could not connect that mailbox',
        detail: data.detail,
        note: data.note,
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className="card mbx-form" onSubmit={submit}>
      <h3>{existing ? 'Reconnect your mailbox' : 'Connect your mailbox'}</h3>
      <p className="mbx-lede">
        Your mail comes into the CRM, files itself against the right account, and you can
        reply from here as yourself. We work out the server settings from your address.
      </p>

      <label>
        <span>Email address</span>
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@fitness1stsports.com"
          autoComplete="username"
          required
        />
      </label>

      <label>
        <span>Password</span>
        <input
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder={existing ? 'Enter it again to reconnect' : 'Your email password'}
          autoComplete="current-password"
          required
        />
      </label>

      <fieldset className="mbx-scope">
        <legend>What should we pull in?</legend>
        <label className="mbx-radio">
          <input type="radio" name="scope" value="all"
                 checked={scope === 'all'} onChange={() => setScope('all')} />
          <span>
            <strong>My whole mailbox</strong>
            <em>Work out of the CRM all day. Mail from CRM contacts files itself to their account.</em>
          </span>
        </label>
        <label className="mbx-radio">
          <input type="radio" name="scope" value="crm_matched"
                 checked={scope === 'crm_matched'} onChange={() => setScope('crm_matched')} />
          <span>
            <strong>Only mail from CRM contacts</strong>
            <em>Everything else is read, matched, and discarded — it never reaches the CRM.</em>
          </span>
        </label>
      </fieldset>

      {error && (
        <div className="banner error mbx-error">
          <strong>{error.message}</strong>
          {error.detail && <span className="mbx-detail">{error.detail}</span>}
          {error.note && <span className="mbx-detail">{error.note}</span>}
        </div>
      )}

      <p className="mbx-fineprint">
        Your password is checked against the mail server before it is saved, then stored
        encrypted. Nobody else in the CRM can read your mailbox — not even a manager.
      </p>

      <div className="mbx-actions">
        <button type="submit" className="btn primary" disabled={busy || !email || !password}>
          {busy ? 'Checking the mail server…' : existing ? 'Reconnect' : 'Connect mailbox'}
        </button>
        {onCancel && (
          <button type="button" className="btn" onClick={onCancel} disabled={busy}>Cancel</button>
        )}
      </div>
    </form>
  );
};

const ConnectedCard = ({ mailbox, onChanged, onReconnect }) => {
  const [busy, setBusy] = useState(null);
  const [note, setNote] = useState(null);
  const [error, setError] = useState(null);

  const checkNow = async () => {
    setBusy('sync'); setError(null); setNote(null);
    try {
      const r = await api.post(`/crm/mailboxes/${mailbox.id}/sync`);
      const got = (r.data.inbox?.stored || 0) + (r.data.sent?.stored || 0);
      setNote(got ? `${got} new message${got === 1 ? '' : 's'} pulled in.` : 'Already up to date.');
      onChanged();
    } catch (err) {
      setError(err.response?.data?.message || 'Could not reach the mail server');
    } finally {
      setBusy(null);
    }
  };

  const disconnect = async () => {
    // eslint-disable-next-line no-alert
    if (!window.confirm('Disconnect this mailbox? Mail already filed against accounts stays in the CRM; new mail stops arriving.')) return;
    setBusy('disconnect'); setError(null);
    try {
      await api.delete(`/crm/mailboxes/${mailbox.id}`);
      onChanged();
    } catch (err) {
      setError(err.response?.data?.message || 'Could not disconnect');
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="card mbx-card">
      <div className="mbx-head">
        <div>
          <h3>{mailbox.email_address}</h3>
          <p className="mbx-sub">
            {mailbox.imap_host}
            {mailbox.sync_scope === 'all' ? ' · whole mailbox' : ' · CRM contacts only'}
            {(mailbox.smtp_host || mailbox.ews_url) ? ' · can send as you' : ' · reading only'}
          </p>
        </div>
        <StatusPill status={mailbox.status} />
      </div>

      {mailbox.status_detail && <p className="mbx-detail-line">{mailbox.status_detail}</p>}

      <dl className="mbx-facts">
        <div><dt>Last checked</dt><dd>{mailbox.last_synced_at ? shortDate(mailbox.last_synced_at) : 'never'}</dd></div>
        <div><dt>Unread here</dt><dd>{mailbox.unread ?? 0}</dd></div>
        <div><dt>Connected</dt><dd>{mailbox.connected_at ? shortDate(mailbox.connected_at) : '—'}</dd></div>
      </dl>

      <p className="mbx-fineprint">
        New mail arrives on its own, usually within seconds. "Check now" is only for when
        you do not want to wait.
      </p>

      {note && <p className="banner mbx-note">{note}</p>}
      {error && <p className="banner error">{error}</p>}

      <div className="mbx-actions">
        <button type="button" className="btn primary" onClick={checkNow} disabled={!!busy}>
          {busy === 'sync' ? 'Checking…' : 'Check now'}
        </button>
        <button type="button" className="btn" onClick={onReconnect} disabled={!!busy}>
          Update password
        </button>
        <button type="button" className="btn danger" onClick={disconnect} disabled={!!busy}>
          {busy === 'disconnect' ? 'Disconnecting…' : 'Disconnect'}
        </button>
      </div>
    </div>
  );
};

const MailboxSetup = ({ mailboxes, onChanged }) => {
  const { user } = useContext(AuthContext);
  const [reconnecting, setReconnecting] = useState(false);
  const [result, setResult] = useState(null);

  const personal = mailboxes.find(m => m.user_id);
  const live = personal && personal.status !== 'not_connected' && personal.has_credential !== false;

  const finish = (data) => {
    setResult(data);
    setReconnecting(false);
    onChanged();
  };

  return (
    <div className="mbx">
      {result && (
        <div className="card mbx-result">
          <h3>Mailbox connected</h3>
          <p>
            {result.test?.inbox?.total != null
              ? `${result.test.inbox.total.toLocaleString()} messages in your inbox, ${result.test.inbox.unseen} unread.`
              : 'Signed in successfully.'}
            {' '}We are pulling in your recent mail now — it will fill in over the next minute or two.
          </p>
          <ul className="mbx-checks">
            <li>{result.test?.tls?.protocol ? `Encrypted with ${result.test.tls.protocol}` : 'Encrypted connection'}</li>
            <li>{result.test?.supportsIdle ? 'Live updates supported — new mail arrives in seconds' : 'New mail is checked periodically'}</li>
            <li className={result.canSend ? '' : 'warn'}>
              {result.canSend
                ? 'You can reply from here as yourself'
                : `Reading only — sending unavailable: ${result.sendError}`}
            </li>
          </ul>
          <button type="button" className="btn" onClick={() => setResult(null)}>Done</button>
        </div>
      )}

      {live && !reconnecting && !result && (
        <ConnectedCard
          mailbox={personal}
          onChanged={onChanged}
          onReconnect={() => setReconnecting(true)}
        />
      )}

      {(!live || reconnecting) && !result && (
        <ConnectForm
          initialEmail={user?.email}
          existing={reconnecting ? personal : null}
          onDone={finish}
          onCancel={reconnecting ? () => setReconnecting(false) : null}
        />
      )}
    </div>
  );
};

export default MailboxSetup;
